import React, { useState } from "react";
import { Button, Card, CardContent, Typography, Box } from "@mui/material";
import { motion } from "framer-motion";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { toast } from "react-toastify";
import axios from "axios";

const EmailSent = () => {
  const [countdown, setCountdown] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const email = sessionStorage.getItem("forgot-email");

  const redactEmail = (email) => {
    const emailParts = email.split("@");
    if (emailParts.length === 2) {
      const username = emailParts[0];
      const domain = emailParts[1];

      // Keep the first 4 characters of the username and redact the rest.
      const redactedUsername =
        username.slice(0, 4) + "*".repeat(username.length - 4);

      return `${redactedUsername}@${domain}`;
    }
    return email;
  };

  const redactedEmail = redactEmail(email);

  const openEmailService = () => {
    const domain = email.split("@")[1].toLowerCase();

    let url = "";

    switch (domain) {
      case "gmail.com":
        url = "https://mail.google.com/mail/";
        break;
      case "yahoo.com":
        url = "https://mail.yahoo.com/";
        break;
      case "outlook.com":
        url = "https://outlook.live.com/";
        break;
      case "hotmail.com":
        url = "https://outlook.live.com/";
        break;
      case "aol.com":
        url = "https://mail.aol.com/";
        break;
      case "icloud.com":
        url = "https://www.icloud.com/mail";
        break;
      default:
        url = `mailto:${email}`;
        break;
    }

    window.open(url, "_blank");
  };

  const handleResendEmail = async (e) => {
    e.preventDefault();

    setIsResendDisabled(true);
    setCountdown(60);

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setIsLoading(true);

    try {
      await axios.post(
        "https://le-travaille-server.onrender.com/auth/forgot-password",
        { email },
      );

      toast.success("Password reset email has been resent!");
      sessionStorage.setItem("forgot-email", email);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
        p: 2,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
        style={{ width: "100%", maxWidth: 450 }}
      >
        <Card
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #003d38 0%, #00786F 100%)",
              color: "white",
              textAlign: "center",
            }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255,215,0,0.15)",
                  borderRadius: "50%",
                  padding: "16px",
                  display: "inline-block",
                  mb: 2,
                  border: "2px solid rgba(255,215,0,0.2)",
                }}
              >
                <MailOutlineIcon sx={{ fontSize: 40, color: "#FFD700" }} />
              </Box>
            </motion.div>

            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Check Your Email
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
              We have sent a password reset link to your email
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {/* Email Display */}
            <Box
              sx={{
                backgroundColor: "#f8fafc",
                borderRadius: 2,
                p: 2,
                mb: 3,
                textAlign: "center",
                border: "1px solid #e8edf2",
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Email sent to:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: "#00786F",
                  wordBreak: "break-all",
                }}
              >
                {redactedEmail}
              </Typography>
            </Box>

            <Box>
              <Button
                variant="contained"
                onClick={openEmailService}
                startIcon={<OpenInNewIcon />}
                fullWidth
                sx={{
                  backgroundColor: "#00786F",
                  color: "#fff",
                  fontWeight: 600,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  "&:hover": {
                    transform: "scale(1.02)",
                  },
                  transition:
                    "transform 0.2s ease-in-out, background-color 0.2s ease",
                }}
              >
                Go to Your Email
              </Button>
            </Box>

            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: "1px solid #e8edf2",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Didn't receive the email?{" "}
                <span
                  onClick={handleResendEmail}
                  style={{
                    color: "#00786F",
                    cursor:
                      isResendDisabled || isLoading ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    textDecoration: "underline",
                    opacity: isResendDisabled || isLoading ? 0.6 : 1,
                  }}
                >
                  {isLoading
                    ? "Resending..."
                    : isResendDisabled
                      ? `Resend Email in ${countdown}s`
                      : "Resend Email"}
                </span>
              </Typography>

              {isResendDisabled && !isLoading && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    mt: 0.5,
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 14, color: "#9aabba" }} />
                  <Typography variant="caption" color="text.secondary">
                    Please wait {countdown} seconds before trying again
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default EmailSent;
